import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { CODED_MESSAGE_VERBATIM_KEYS } from '../models/coded-status-message.model';
import { GbeEndPointV4 } from '../models/gbe/gbe-endpoint.enum';
import { GBE_BASE_URL } from '../tokens/gbe-url.token';
import { convertKeysToCamelCase, urlMatchesEndpoint } from '../utils/case-conversions';

export const apiResponseCaseInterceptor: HttpInterceptorFn = (req, next) => {
  // add other apis if needed
  const gbeBaseUrl = inject(GBE_BASE_URL);

  const snakeCaseApis: string[] = [gbeBaseUrl];
  const ignoreUrls: string[] = [];
  const ignoredKeysPerUrl = new Map<string, string[]>();

  // we don't want to temper with the original json schemas returned by GBE
  const transformerUrl = gbeBaseUrl + '/' + GbeEndPointV4.TRANSFORMERS;
  ignoredKeysPerUrl.set(transformerUrl, ['schema']);

  // The get-names response nests resolved names under uid keys; those keys must
  // survive verbatim (a uid like `site_01` must not become `site01`). Registered
  // before the generic data entry because it also matches the `v4/data` prefix.
  const getNamesUrl = gbeBaseUrl + '/' + GbeEndPointV4.DATA_GET_NAMES;
  ignoredKeysPerUrl.set(getNamesUrl, ['data']);

  // we don't want to temper with the original structure names returned by the API
  const dataUrl = gbeBaseUrl + '/' + GbeEndPointV4.DATA;
  ignoredKeysPerUrl.set(dataUrl, ['result']);

  // GFE preferences are a free-form blob stored verbatim by GBE; keep its keys
  // untouched so the camelCase payload round-trips unchanged.
  const userPreferencesUrl = gbeBaseUrl + '/' + GbeEndPointV4.USER_PREFERENCES;
  ignoredKeysPerUrl.set(userPreferencesUrl, ['preferences']);

  // Entity `extras` is a free-form metric map (e.g. `fleet_size`); keep its keys
  // snake_case so they can be used verbatim as i18n label keys.
  const entitiesUrl = gbeBaseUrl + '/' + GbeEndPointV4.ENTITIES;
  ignoredKeysPerUrl.set(entitiesUrl, ['extras']);

  // A recipe's parameter schema is a JSON Schema document whose `properties`
  // keys are parameter names (e.g. `target_date`). Camel-casing them would
  // desynchronise them from `required` and `x-parameter-order`, which hold the
  // same names as string *values* and so survive conversion untouched.
  // `filters` is a free-form filter configuration stored verbatim by GBE; the
  // admin editor shows it raw, so its keys must reach the form as served.
  const recipesUrl = gbeBaseUrl + '/' + GbeEndPointV4.RECIPES;
  ignoredKeysPerUrl.set(recipesUrl, ['schema', 'filters']);

  // A preset's path filter is the data layer's grammar, whose keys (`date_time`,
  // `is_none_of`) are part of the wire format and must come back as stored.
  const filterPresetsUrl = gbeBaseUrl + '/' + GbeEndPointV4.FILTER_PRESETS;
  ignoredKeysPerUrl.set(filterPresetsUrl, ['path_filter']);

  // A solver parameter set's `data` is a free-form tuning blob stored verbatim
  // by GBE and shown raw in the admin JSON editor.
  const solverParametersUrl = gbeBaseUrl + '/' + GbeEndPointV4.SOLVER_PARAMETERS;
  ignoredKeysPerUrl.set(solverParametersUrl, ['data']);

  // Plan `extras_per_type` is a free-form metric map per entity type (e.g.
  // `{ vehicle: { fleet_size: 12 } }`); keep its inner keys snake_case so they
  // can be used verbatim as i18n label keys (same trick as entity `extras`).
  // `recipe_parameters` is keyed by parameter name and must survive verbatim so
  // the values can be resolved against the recipe's schema.
  // `details` is registered per URL rather than globally — only the handful of 2xx bodies
  // that carry a `CodedStatusMessage` need it, and exempting the key everywhere would
  // strip case conversion from unrelated responses that happen to use the name.
  const plansUrl = gbeBaseUrl + '/' + GbeEndPointV4.PLANS;
  ignoredKeysPerUrl.set(plansUrl, [
    'extras_per_type',
    'recipe_parameters',
    ...CODED_MESSAGE_VERBATIM_KEYS
  ]);

  // Plan summaries are served under the planning-space route and carry the same
  // parameter-name-keyed `recipe_parameters` map, and the same plan `warnings`.
  const planningSpacesUrl = gbeBaseUrl + '/' + GbeEndPointV4.PlANNINGSPACES;
  ignoredKeysPerUrl.set(planningSpacesUrl, [
    'recipe_parameters',
    ...CODED_MESSAGE_VERBATIM_KEYS
  ]);

  // A deploy configuration's `/messages` response carries a `CodedStatusMessage` per
  // message and per configuration, whose `details.context` is what turns a warning into
  // a link to the entity at fault.
  const deployPlanConfigUrl = gbeBaseUrl + '/' + GbeEndPointV4.DEPLOY_PLAN_CONFIG;
  ignoredKeysPerUrl.set(deployPlanConfigUrl, [...CODED_MESSAGE_VERBATIM_KEYS]);

  const isTargeted =
    !ignoreUrls.includes(req.url) &&
    snakeCaseApis.some((snakeCaseUrl) => req.url.startsWith(snakeCaseUrl));

  if (!isTargeted) {
    return next(req);
  }

  return next(req).pipe(
    // Any endpoint can fail with a coded error, so the error path keeps `details`
    // verbatim whatever the URL — unlike the success path, where only the two
    // registered above ever carry one.
    catchError((error: HttpErrorResponse) => {
      if (error.error instanceof Blob) {
        return throwError(() => error);
      }
      return throwError(
        () =>
          new HttpErrorResponse({
            error: convertKeysToCamelCase(error.error, CODED_MESSAGE_VERBATIM_KEYS),
            headers: error.headers,
            status: error.status,
            statusText: error.statusText,
            url: error.url as string
          })
      );
    }),
    map((event) => {
      if (event instanceof HttpResponse) {
        // A Blob has no enumerable own properties, so the key conversion would
        // rebuild it as `{}` and silently empty the downloaded file.
        if (event.body instanceof Blob) {
          return event;
        }
        const perUrl =
          [...ignoredKeysPerUrl.entries()].find(([url]) =>
            urlMatchesEndpoint(req.url, url)
          )?.[1] ?? [];
        return event.clone({
          body: convertKeysToCamelCase(event.body as object, perUrl)
        });
      }
      return event;
    })
  );
};
